<template>
	<div class="chat-container">
		<div
			:class="[
				'chat-container__chat-message-wrapper',
				{
					'chat-message--user': roleLabel === 'user',
					'chat-message--error': roleLabel === 'system-error'
				}
			]"
			v-if="hasMessage"
		>
			<div class="chat-container__chat-message">
				<i class="icon icon-vue" v-if="isAssistent"></i>
				<span class="chat-container__error-icon"
					><i class="icon icon-warning" v-if="isAnErrorSystem"></i
				></span>
				<div
					class="chat-message__text-container"
					@mouseover="showCopyIcon = true"
					@mouseleave="showCopyIcon = false"
				>
					<p
						v-if="!message.isLoading"
						class="chat-message__text"
						v-html="renderedContent"
					></p>

					<Spinner class="chat-message__text" v-else />
					<i
						v-if="showCopyIcon && isAssistent"
						class="icon icon-copy"
						@click="copyToClipboard"
					></i>
				</div>
			</div>
			<p class="chat-container__time">{{ formattedTime }}</p>
		</div>
	</div>
</template>

<script>
import { getTimeFromISO12Hour } from '@/utils/date-functions';
import Spinner from './vue-spinner.vue';
import _ from 'lodash';
import MarkdownIt from 'markdown-it';

export default {
	name: 'VueChatMessage',
	components: {
		Spinner
	},
	props: {
		message: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			showCopyIcon: false
		};
	},
	computed: {
		formattedTime() {
			return getTimeFromISO12Hour(this.message.timestamp);
		},
		hasMessage() {
			return !_.isEmpty(this.message);
		},
		roleLabel() {
			return this.message.role?.label || '';
		},
		renderedContent() {
			const md = new MarkdownIt({
				html: true,
				linkify: true,
				typographer: true
			});

			const content = this.message.content.trim();
			let htmlContent = md.render(content);

			// Define the conditions and replacements
			const replacements = {
				'<a ': '<a class="chat-message__link" target="_blank" ',
				'<ul>': '<ul class="chat-message__unordered-list">',
				'<ol>': '<ol class="chat-message__ordered-list">',
				'<li>': '<li class="chat-message__list-item">',
				'<h3>': '<h3 class="chat-message__section-heading">'
			};

			// Apply the replacements
			for (const [key, value] of Object.entries(replacements)) {
				htmlContent = htmlContent.replace(new RegExp(key, 'g'), value);
			}

			return htmlContent;
		},
		isAssistent() {
			return this.message.role?.label === 'assistant';
		},
		isAnErrorSystem() {
			return this.message.role?.label === 'system-error';
		}
	},
	methods: {
		copyToClipboard() {
			const el = document.createElement('textarea');
			el.value = this.message.content;
			document.body.appendChild(el);
			el.select();
			document.execCommand('copy');
			document.body.removeChild(el);
		}
	}
};
</script>

<style lang="scss">
.chat-message__link {
	color: var(--chat-link);
}
.chat-message__ordered-list,
.chat-message__unordered-list {
	padding-left: $padding-md;
}

.chat-message__list-item > p {
	padding: $padding-xs;
	font-size: $txt-font-size;
}

.chat-message__unordered-list .chat-message__list-item::marker {
	content: '• ';
}

.chat-message__ordered-list .chat-message__list-item::marker {
	content: counter(list-item) '.  '; /* Custom number for ordered list */
	counter-increment: list-item;
}
/* Style for unordered lists inside ordered lists */
.chat-message__ordered-list
	.chat-message__unordered-list
	.chat-message__list-item::marker {
	content: '• ';
}

/* Style for ordered lists inside unordered lists */
.chat-message__unordered-list
	.chat-message__ordered-list
	.chat-message__list-item::marker {
	content: counter(list-item) '-';
	counter-increment: list-item;
}
.chat-message__text > p {
	font-size: $txt-font-size;
	line-height: 18px;
}
.chat-message__section-heading {
	font-size: 16px;
	padding-top: $padding-sm;
	font-weight: bold;
}
</style>

<style lang="scss" scoped>
.chat-container {
	&__chat-message-wrapper {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		margin-bottom: $gutter-md;
		padding-right: $padding-sm;

		&.chat-message--user {
			align-items: flex-end;
		}
	}

	&__chat-message {
		display: flex;
		flex-direction: row;
		margin-bottom: $gutter-xs;

		.chat-message__text {
			background-color: var(--neutral-1);
			padding: $padding-sm $padding-lg $padding-sm $padding-sm;
			border-radius: 0 $border-radius $border-radius $border-radius;
			font-size: $txt-font-size;
			line-height: 18px;
			margin-left: $gutter-sm;
			min-height: 32px;
		}

		.chat-message__text-container {
			position: relative;
		}

		.icon-copy {
			position: absolute;
			top: 5px;
			right: 5px;
			cursor: pointer;
			display: none;
		}

		.chat-message__text-container:hover .icon-copy {
			display: inline;
		}
	}

	.chat-message--user .chat-message__text {
		background-color: #b3ccbf;
		border-radius: $border-radius 0 $border-radius $border-radius;
	}
	.chat-message--error .chat-message__text {
		background-color: var(--ui-negative-light);
		border-radius: 0 $border-radius $border-radius $border-radius;
		border: 1px solid var(--ui-negative);
	}

	&__time {
		text-align: left;
		font-size: $field-help-font-size;
		color: var(--neutral-3);
		margin: $gutter-sm 0 $gutter-md 20px;
	}
	&__error-icon {
		color: var(--ui-negative);
	}
}
</style>
